import { createContext, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import useAppState from '../hooks/appStateHook';
import { AngularContext } from './AngularProvider';
import PropTypes from 'prop-types';
import { ensureArray } from './arrayUtilities';

export const BroadcastContext = createContext({});

export const BroadcastProvider = ({ children }) => {
    const { token } = useAppState();
    const eventListeners = useRef({});
    const universalListeners = useRef([]);
    const childWindows = useRef([]);
    
    const Angular = useContext(AngularContext);
    const $rootScope = Angular?.$rootScope;

    // Function to broadcast events to subscribed listeners
    const broadcast = useCallback((eventName, data, excludeWindowPropagation) => {
        eventListeners.current[eventName]?.forEach(listener => listener(data));
        universalListeners.current?.forEach(listener => listener(eventName, data));
        if ($rootScope) {
            //$rootScope.$broadcast(eventName, data);
        }
        if (!excludeWindowPropagation) {
            if (window.opener) {
                window.opener.postMessage({ type: eventName, params: data });
            }
            childWindows.current.forEach(child => {
                if (!child.closed) {
                    child.postMessage({ type: eventName, params: data })
                }
            });
            childWindows.current = childWindows.current.filter(child => !child.closed);
        }
    },[$rootScope]);

    useEffect(() => {
        const handler = event => {
            // pass true to not send the message back to the parent window
            if (event.data.type && event.data.params) {
                broadcast(event.data.type, event.data.params, true);
            }
        }
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [broadcast]);

    // Subscribe and unsubscribe to Angular events
    useEffect(() => {
        const unsubscribeHandlers = Object.keys(eventListeners.current).map(eventName => {
            const handler = (event, data) => {
                eventListeners.current[eventName]?.forEach(listener => listener(data));
                universalListeners.current?.forEach(listener => listener(eventName, data));
            };

            // Subscribe to the Angular $rootScope event
            const cleanup = $rootScope ? $rootScope.$on(eventName, handler) : () => {};

            // Return the unsubscribe function for this handler
            return () => cleanup();
        });

        // Clean up all subscriptions when the component unmounts or listeners change
        return () => {
            unsubscribeHandlers.forEach(unsubscribe => unsubscribe());
        };
    }, [$rootScope]); // Re-subscribe when $rootScope or listeners change

    // Function to subscribe to an event
    const subscribe = useCallback((eventName, callback) => {
        const listenersForEvent = eventListeners.current[eventName];
        if (!listenersForEvent?.includes(callback)) {
            eventListeners.current[eventName] = [ ...ensureArray(listenersForEvent), callback ];
            if (!listenersForEvent && $rootScope) {
                $rootScope.$on(eventName, (event, data) => {
                    eventListeners.current[eventName]?.forEach(listener => listener(data));
                })
            }
        }

        return () => {
            eventListeners.current[eventName] = eventListeners.current[eventName]?.filter(c => c !== callback);
        }
    }, [$rootScope]);

    const subscribeAll = useCallback(callback => {
        if (!universalListeners.current?.includes(callback)) {
            universalListeners.current.push(callback);
        }

        return () => {
            universalListeners.current = universalListeners.current.filter(c => c !== callback);
        }
    }, []);

    const opener = useCallback((url, target, options) => {
        const child = window.open(url, target, options);
        if (childWindows.current.every(c => c !== child)) {
            childWindows.current.push(child);
            child.addEventListener("load", function () {
                // need a timeout to ensure that the child is listening before we fire the state synchornization
                setTimeout(() => {
                    broadcast('childOpened', { child }, true);
                }, 500);
            })
        }
        childWindows.current = childWindows.current.filter(child => !child.closed);
    }, [broadcast]);

    const popout = useCallback((sid, args) => {
        opener((process.env.NODE_ENV === 'production' ? process.env.PUBLIC_URL : '') + '/popout/' + sid + '?token=' + token + (args ? '&args=' + encodeURIComponent(JSON.stringify(args)) : ''), '_blank', 'popup=1')
    }, [opener, token])

    const value = useMemo(() => ({ broadcast, subscribe, subscribeAll, opener, popout }), [broadcast, subscribe, subscribeAll, opener, popout]);

    return (
        <BroadcastContext.Provider value={value}>
            {children}
        </BroadcastContext.Provider>
    );
};

BroadcastProvider.propTypes = {
    children: PropTypes.node
}